import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Megaphone } from "lucide-react";
import api from "@/lib/api";

const ILLUSTRATION =
  "https://customer-assets-jai6qajn.emergentagent.net/job_ai-solutions-lab-22/artifacts/5k3y0wa0_h9-rev-img2.png";

const AUTO_ADVANCE_MS = 6500;

export default function WhatsLatest() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const pausedRef = useRef(false);

  useEffect(() => {
    api.get("/news").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (items.length < 2) return;
    const t = setInterval(() => {
      if (!pausedRef.current) setIdx((i) => (i + 1) % items.length);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(t);
  }, [items.length]);

  if (items.length === 0) return null;

  const item = items[idx];
  const go = (dir) => setIdx((i) => (i + dir + items.length) % items.length);

  return (
    <section className="relative py-12 md:py-16" data-testid="whats-latest-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* LEFT — headline + illustration */}
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 mb-4">
              <Megaphone className="w-4 h-4 text-primary" strokeWidth={2.2} />
              <span className="text-xs font-mono uppercase tracking-[0.28em] text-primary">
                What&apos;s Latest
              </span>
            </div>
            <h2
              className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02]"
              data-testid="whats-latest-title"
            >
              News that <span className="italic text-primary">actually moves</span> the needle.
            </h2>
            <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-md">
              Launches, milestones, awards, partnerships — the human story behind CloudLab, straight from the team.
            </p>

            <div className="relative mt-8 aspect-[4/3] max-w-lg">
              <div
                aria-hidden="true"
                className="absolute -inset-6 rounded-[3rem] opacity-70 blur-2xl"
                style={{
                  background:
                    "radial-gradient(60% 60% at 40% 40%, hsl(var(--primary) / 0.35), transparent 70%), radial-gradient(60% 60% at 80% 70%, hsl(var(--secondary) / 0.35), transparent 70%)",
                }}
              />
              <img
                src={ILLUSTRATION}
                alt="Team leading with a megaphone"
                className="relative w-full h-full object-contain"
                loading="lazy"
                data-testid="whats-latest-illustration"
              />
            </div>
          </div>

          {/* RIGHT — creative news slider */}
          <div className="lg:col-span-7">
            <div
              className="relative"
              onMouseEnter={() => { pausedRef.current = true; }}
              onMouseLeave={() => { pausedRef.current = false; }}
              data-testid="whats-latest-slider"
            >
              <div className="relative overflow-hidden rounded-[2rem] border border-border bg-card shadow-[0_30px_60px_-24px_rgba(15,23,42,0.25)]">
                <AnimatePresence mode="wait">
                  <motion.article
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.4 }}
                    className="relative"
                    data-testid={`news-slide-${idx}`}
                  >
                    <div className="relative aspect-[16/9] overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div
                        aria-hidden="true"
                        className="absolute inset-0"
                        style={{
                          background:
                            "linear-gradient(180deg, transparent 45%, rgba(15,23,42,0.65) 100%)",
                        }}
                      />
                      {item.tag && (
                        <div className="absolute top-5 left-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm text-[10px] font-mono uppercase tracking-[0.22em] text-slate-900">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                          {item.tag}
                        </div>
                      )}
                    </div>
                    <div className="p-6 md:p-8">
                      <h3 className="font-heading font-bold text-2xl md:text-3xl leading-tight">
                        {item.title}
                      </h3>
                      <p className="mt-3 text-base text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                      {item.link && (
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-primary hover:gap-2 transition-all"
                        >
                          Read the full story →
                        </a>
                      )}
                    </div>
                  </motion.article>
                </AnimatePresence>
              </div>

              {/* Controls + pagination */}
              <div className="mt-5 flex items-center justify-between">
                <div className="flex gap-1.5" role="tablist" aria-label="Select news slide">
                  {items.map((_, i) => (
                    <button
                      key={i}
                      role="tab"
                      aria-selected={i === idx}
                      onClick={() => setIdx(i)}
                      className={`h-1.5 rounded-full transition-all ${
                        i === idx ? "w-8 bg-primary" : "w-4 bg-muted hover:bg-primary/40"
                      }`}
                      data-testid={`news-dot-${i}`}
                    />
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => go(-1)}
                    aria-label="Previous news"
                    data-testid="news-prev"
                    className="w-10 h-10 rounded-full border border-border hover:bg-muted transition-colors flex items-center justify-center"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => go(1)}
                    aria-label="Next news"
                    data-testid="news-next"
                    className="w-10 h-10 rounded-full border border-border hover:bg-muted transition-colors flex items-center justify-center"
                  >
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
